import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Code, 
  Palette, 
  BarChart3,
  Users,
  Heart,
  Coffee,
  Briefcase,
  MapPin,
  ArrowLeft,
  ArrowRight,
  Zap,
  Globe,
  Rocket,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Careers = () => {
  const benefits = [
    { icon: Heart, title: "Health Coverage", description: "Medical, dental, vision—the works" },
    { icon: Coffee, title: "Remote-Friendly", description: "Work from wherever makes you productive" },
    { icon: Zap, title: "Learning Budget", description: "Level up your skills on our dime" },
    { icon: Users, title: "Team Hangs", description: "Regular events that aren't boring" },
    { icon: Globe, title: "Travel Opps", description: "Work with people across the country" },
    { icon: Rocket, title: "Equity", description: "Own a piece of what we're building" },
  ];

  const openings = [
    {
      category: "Engineering",
      icon: Code,
      positions: [
        { title: "Backend Engineer", location: "Remote", type: "Full-time" },
        { title: "Mobile Engineer (iOS/Android)", location: "Remote", type: "Full-time" },
        { title: "Data Engineer", location: "Remote", type: "Full-time" },
      ]
    },
    {
      category: "Product & Design",
      icon: Palette,
      positions: [
        { title: "Product Designer", location: "Remote", type: "Full-time" },
        { title: "UX Researcher", location: "Remote", type: "Full-time" },
      ]
    },
    {
      category: "Operations",
      icon: BarChart3,
      positions: [
        { title: "City Launcher", location: "Multiple Cities", type: "Full-time" },
        { title: "Driver Success", location: "Remote", type: "Full-time" },
      ]
    },
    {
      category: "Business",
      icon: Briefcase,
      positions: [
        { title: "Growth Marketing", location: "Remote", type: "Full-time" },
        { title: "Partnerships", location: "Remote", type: "Full-time" },
      ]
    },
  ];

  const values = [
    "Ship fast, learn faster",
    "Own your work",
    "No ego, just impact",
    "Stay curious",
    "Build for real people"
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-red-600/20 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            Build Something Real
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            We're a small team with big ambitions. Just launched in 2025 and already shaking things up. 
            If you want to actually see your work matter, you're in the right place.
          </p>
          <a href="#openings">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg gap-2">
              See Open Roles
              <ArrowRight className="w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* The Pitch */}
      <section className="py-12 px-6 md:px-12 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-bold text-white">
            "We're not here to move fast and break things. We're here to build something 
            that actually helps people—drivers, riders, everyone."
          </p>
          <p className="text-red-100 mt-4">— The Reddi Team</p>
        </div>
      </section>

      {/* Culture Values */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8 justify-center">
            <Sparkles className="w-8 h-8 text-amber-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              How We Work
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {values.map((value, index) => (
              <div 
                key={index}
                className="px-6 py-3 bg-slate-800/50 border border-slate-700 text-white hover:border-red-500/50 transition-colors"
              >
                {value}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            The Perks
          </h2>
          <p className="text-slate-400 text-center mb-12">
            We take care of our people. That's not just talk.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-slate-800/50 border border-slate-700"
              >
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Now */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Rocket className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            Why Join Now?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Early stage means high impact. You won't be a cog in a machine—you'll be shaping what this company becomes. 
            Plus, early employees get equity. Just saying.
          </p>
        </div>
      </section>

      {/* Open Positions */}
      <section id="openings" className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-12 text-center">
            Open Roles
          </h2>
          <div className="space-y-8">
            {openings.map((category, index) => (
              <div key={index}>
                <div className="flex items-center gap-3 mb-4">
                  <category.icon className="w-6 h-6 text-red-500" />
                  <h3 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase">
                    {category.category}
                  </h3>
                </div>
                <div className="space-y-3">
                  {category.positions.map((position, posIndex) => (
                    <div 
                      key={posIndex}
                      className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 hover:border-red-500/50 transition-colors cursor-pointer group"
                    >
                      <div>
                        <h4 className="text-white font-medium group-hover:text-red-400 transition-colors">
                          {position.title}
                        </h4>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-slate-400 text-sm flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {position.location}
                          </span>
                          <span className="text-slate-500 text-sm">{position.type}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-red-500 transition-colors" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-12 text-center">
            Our Process
          </h2>
          <p className="text-slate-400 text-center mb-12">
            We keep it simple. No 8-round interview marathons.
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Apply", desc: "Send us your stuff" },
              { step: 2, title: "Chat", desc: "Quick call with the team" },
              { step: 3, title: "Deep Dive", desc: "Show us what you got" },
              { step: 4, title: "Offer", desc: "Welcome aboard" },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
            Don't See Your Role?
          </h2>
          <p className="text-slate-400 mb-8">
            We're always looking for exceptional people. Drop us a line—let's talk.
          </p>
          <Link to="/contact">
            <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Careers;
